// src/utils/total.ts
const DEBUG_TOTAL = false;
export const tlog = (...a: any[]) => DEBUG_TOTAL && console.log("[TOTAL]", ...a);
export const tgrp = (title: string, fn: () => void) => {
  if (!DEBUG_TOTAL) return fn();
  console.group(title);
  try { fn(); } finally { console.groupEnd(); }
};

export const getMinPrice = (
  priceArray: any[],
  startDate: string,
  endDate: string,
  discountPct: number = 0
) => {
  const prices = priceArray
    .filter((p) => p.start_date >= startDate && p.end_date <= endDate)
    .map((p) => {
      const base = Number(p.price_excluding_tax);
      const factor = 1 - (Number(discountPct) || 0) / 100;
      return discountPct > 0 ? base * factor : base;
    });

  return prices.length > 0 ? Math.min(...prices) : 0;
};

export const sumPrice = (priceArray: any[]) => {
  const sum = priceArray.reduce((acc, price) => acc + Number(price || 0), 0);
  const formattedSum = sum.toFixed(2);
  return formattedSum;
};

// Applique une remise % à un tableau de prix par nuit
export const applyDiscountToNights = (prices: number[] = [], discountPct: number = 0) => {
  const factor = 1 - (Number(discountPct) || 0) / 100;
  return (prices || []).map((v) => Number((Number(v || 0) * factor).toFixed(2)));
};

// Fabrique la structure MEWS “TimeUnitPrices”
export const buildTimeUnitPrices = (prices: number[] = []) => {
  return prices.map((v, idx) => ({
    Index: idx,
    Amount: {
      Currency: "EUR",
      GrossValue: Number(v),
      TaxCodes: ["FR-S"],
    },
  }));
};

export const TotalPrice = (values: any) => {
  let prix_chambre: number[] = [0];

  // === Chambre : prix bruts selon config ===
  if (values?.Step3?.selectedRoom && Object.keys(values.Step3.selectedRoom).length !== 0) {
    if (values.Step1.NbCuriste === "2" && Number(values.Step1.Accompagnant) === 0) {
      prix_chambre = values.Step3.selectedRoom.price.price_two_curists;
    } else if (values.Step1.NbCuriste === "1" && Number(values.Step1.Accompagnant) === 1) {
      prix_chambre = values.Step3.selectedRoom.price.price_curist_companion;
    } else if (values.Step1.NbCuriste === "1" && Number(values.Step1.Accompagnant) === 0) {
      prix_chambre = values.Step3.selectedRoom.price.prices_single;
    }
  } else if (Array.isArray(values?.Step3?.Price)) {
    prix_chambre = values.Step3.Price; // fallback
  }

  // === Curistes comptés dans le 1er terme (le 2e via Step2.Price) ===
  let NbCuriste = Number(values?.Step1?.NbCuriste) || 1;
  if (values?.Step2?.Value === "2" || values?.Step2?.Value === "1") NbCuriste = 1;

  // === Paramètres ===
  const discountPct = Number(values?.Discount) || 0;
  const discountFactor = 1 - discountPct / 100;
  const nbNight = Number(values?.nbNight) || 0;

  // === Cures (remisées) ===
  const cure1BasePerNight = Number(values?.CurePrice) || 0;
  const cure2BasePerNight = Number(values?.Step2?.Price) || 0;
  const cure1PerNight = cure1BasePerNight * discountFactor;
  const cure2PerNight = cure2BasePerNight * discountFactor;
  const cure1Total = cure1PerNight * NbCuriste * nbNight;
  const cure2Total = cure2PerNight * nbNight;

  // === Chambre (remise par nuit) ===
  const roomPricesDiscounted = applyDiscountToNights(prix_chambre, discountPct);
  const roomTotal = Number(sumPrice(roomPricesDiscounted));

  // === Restauration (petit-déj / repas) ===
  const personsForMeals =
    values?.Step1?.NbCuriste !== "1" || Number(values?.Step1?.Accompagnant) === 1 ? 2 : 1;

  let restaurationBase = 0;
  if (values?.Restauration?.ProductPrice != null) {
    const price = Number(values.Restauration.ProductPrice) || 0;
    const chargingMode = values?.Restauration?.ChargingMode;
    switch (chargingMode) {
      case "PerPersonPerTimeUnit":
        restaurationBase = price * personsForMeals * nbNight;
        break;
      case "PerTimeUnit":
        restaurationBase = price * nbNight;
        break;
      case "PerPerson":
        restaurationBase = price * personsForMeals;
        break;
      case "Once":
      default:
        restaurationBase = price;
        break;
    }
  }
  const restaurationTotal = restaurationBase * discountFactor;

  // === Options additionnelles (exclut la restauration… sauf “Repas package” qui suit la remise globale) ===
  const additionalLines = Array.isArray(values?.AdditionalProducts) ? values.AdditionalProducts : [];

  const restaurationIds = new Set(
    [
      values?.Restauration?.ProductId,
      values?.Restauration?.Id,
      values?.Restauration?.ProductCode,
      values?.Restauration?.Code,
    ]
      .filter(Boolean)
      .map(String)
  );

  const MEAL_RX = /(petit.?d(e|é)j|d(e|é)jeuner|repas|breakfast|meal|restauration)/i;

  const isMealLine = (line: any) => {
    const id = line?.ProductId ?? line?.Id;
    const code = line?.ProductCode ?? line?.Code;
    const name = (line?.ProductName ?? line?.Name ?? line?.name ?? "").toString();
    const category = (line?.Category ?? line?.category ?? "").toString();

    if (id && restaurationIds.has(String(id))) return true;
    if (code && restaurationIds.has(String(code))) return true;
    return MEAL_RX.test(name) || MEAL_RX.test(category);
  };

  // Reconnaît “Repas package”
  const isRepasPackage = (line: any) => {
    const name = (line?.ProductName ?? line?.Name ?? line?.name ?? "").toString();
    return /repas/i.test(name) && /package/i.test(name);
  };

  let additionalFromLines = 0;
  if (additionalLines.length) {
    additionalFromLines = additionalLines.reduce((acc: number, l: any) => {
      // On saute les repas… SAUF “Repas package” qu’on garde dans le calcul
      if (isMealLine(l) && !isRepasPackage(l)) return acc;

      const unit =
        Number(l?.Price ?? l?.price ?? l?.ProductPrice ?? l?.price_excluding_tax ?? 0) || 0;
      if (!isFinite(unit) || unit <= 0) return acc;

      const qty = Number(l?.Quantity ?? l?.Qty ?? l?.quantity ?? 1) || 1;
      const mode = (l?.ChargingMode ?? l?.chargingMode ?? l?.ChargeMode ?? l?.chargeMode ?? "Once") as
        | "PerPersonPerTimeUnit"
        | "PerTimeUnit"
        | "PerPerson"
        | "Once";

      let lineTotal = 0;
      switch (mode) {
        case "PerPersonPerTimeUnit": {
          lineTotal = unit * personsForMeals * nbNight * qty;
          break;
        }
        case "PerTimeUnit": {
          lineTotal = unit * nbNight * qty;
          break;
        }
        case "PerPerson": {
          lineTotal = unit * personsForMeals * qty;
          break;
        }
        case "Once":
        default: {
          lineTotal = unit * qty;
          break;
        }
      }

      // 👉 Application de la remise GLOBALE sur "Repas package" seulement
      if (isRepasPackage(l) && discountPct > 0) {
        lineTotal = Number((lineTotal * discountFactor).toFixed(2));
      }

      return acc + (isFinite(lineTotal) ? lineTotal : 0);
    }, 0);
  }

  // Total agrégé éventuellement fourni (si on n'a pas de lignes détaillées)
  const additionalProvided = Number(values?.AdditionalProductsPrice ?? 0);

  // Source du total additionnel :
  // 1) si calculable via les lignes (hors restauration), on privilégie ça
  // 2) sinon on part de l’agrégé ; par protection on retire la restauration si on la soupçonne incluse
  let additionalTotal =
    additionalFromLines > 0 ? additionalFromLines : additionalProvided;

  if (additionalFromLines === 0 && (values?.Restauration?.ProductPrice ?? null) != null) {
    additionalTotal = Math.max(0, additionalTotal - restaurationTotal);
  }

  // === TOTAL ===
  const Total =
    cure1Total +
    cure2Total +
    roomTotal +
    restaurationTotal +
    additionalTotal;

  // === LOGS ===
  tgrp("TotalPrice()", () => {
    tlog("Réduction globale :", { discountPct, discountFactor });
    tlog("Séjour :", { nbNight, NbCuriste, personsForMeals });

    tgrp("Chambre", () => {
      tlog("Brut / nuit :", prix_chambre);
      tlog("Remisé / nuit :", roomPricesDiscounted);
      tlog("Total chambre :", roomTotal);
    });

    tgrp("Cures", () => {
      tlog(
        "Cure1 base €/nuit :", cure1BasePerNight,
        "→ remisé :", cure1PerNight, "×", NbCuriste, "×", nbNight, "=", cure1Total
      );
      tlog(
        "Cure2 base €/nuit :", cure2BasePerNight,
        "→ remisé :", cure2PerNight, "×", nbNight, "=", cure2Total
      );
    });

    tgrp("Restauration (petit-déj/repas)", () => {
      tlog("ChargingMode :", values?.Restauration?.ChargingMode);
      tlog("ProductPrice :", values?.Restauration?.ProductPrice);
      tlog("Base (avant remise) :", restaurationBase);
      tlog("Total (après remise) :", restaurationTotal);
      tlog("Sélection :", values?.Restauration);
    });

    tgrp("Options", () => {
      tlog("AdditionalProductsPrice (agrégé) :", additionalProvided);
      tlog("Additional (depuis lignes, hors restauration) :", additionalFromLines);
      tlog("Additional retenu :", additionalTotal);
      tlog("Lignes options :", additionalLines);
    });

    tlog("TOTAL (num) :", Total, "→ affichage :", Total.toFixed(2).replace(".", ","));
  });

  return Total.toFixed(2).replace(".", ",");
};

export const getCategoryTitle = (categoryArray: any[]) => {
  const categoryTitle = categoryArray.map((p) => p.title);
  return categoryTitle;
};
